import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, Mail, Ship, ZoomIn, X } from 'lucide-react';
import { useArtifacts } from '../hooks/useArtifacts';
import { CatalogCard } from '../components/ArtifactCards';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';
import { useSiteContent } from '../context/SiteContentContext';

export default function UpcomingLots() {
  const { artifacts, loading } = useArtifacts();
  const { content } = useSiteContent();
  const [zoomArt, setZoomArt] = useState(null);

  const closeZoom = () => setZoomArt(null);

  // Close the lightbox with the Escape key. The lightbox is a pure state-based
  // overlay — it never touches browser history, so Back navigation and page
  // refresh always keep the full Auction Preview list intact.
  useEffect(() => {
    if (!zoomArt) return;
    const onKey = (e) => { if (e.key === 'Escape') setZoomArt(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomArt]);
  const upcoming = artifacts
    .filter((a) => a.status === 'upcoming')
    .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));

  return (
    <div className="min-h-screen bg-luxury-black text-white" data-testid="upcoming-page">
      <Seo
        path="/upcoming"
        title="Upcoming Auction Preview | Lunar Legacies"
        description="Preview lots being prepared for the next Lunar Legacies space memorabilia auction. Astronaut artifacts, flown items, mission hardware and documents — join the Collector List for early access."
      />
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 border-b border-white/5 bg-gradient-to-b from-luxury-charcoal to-luxury-black">
        <div className="max-w-6xl mx-auto text-center">
          <span className="text-xs uppercase tracking-[0.3em] text-luxury-gold font-semibold flex items-center justify-center gap-2 mb-4">
            <CalendarClock size={14} /> Auction Preview
          </span>
          <h1 className="text-4xl sm:text-5xl font-serif-luxury text-white tracking-tight mb-5">Upcoming Auction Preview</h1>
          {content?.upcoming_auction_date ? (
            <div className="inline-flex items-center gap-2.5 border border-luxury-gold/40 bg-luxury-gold/10 px-6 py-3 mb-6" data-testid="upcoming-auction-date">
              <CalendarClock size={18} className="text-luxury-gold" />
              <span className="text-luxury-goldLight font-serif-luxury text-lg md:text-xl tracking-wide">{content.upcoming_auction_date}</span>
            </div>
          ) : null}
          <p className="max-w-2xl mx-auto text-sm md:text-base text-luxury-muted leading-relaxed font-sans-luxury">
            A preview of items being prepared for the upcoming auction. More lots are added as the sale approaches, each researched and documented before it appears here. Join the Collector List for early access, or contact us for details, condition reports, and estimates on any piece.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-20 text-luxury-gold animate-pulse font-serif-luxury tracking-widest">LOADING LOTS...</div>
          ) : upcoming.length === 0 ? (
            <div className="text-center py-16 text-luxury-muted border border-white/5 bg-luxury-charcoal font-sans-luxury text-sm">
              The next auction is being finalized. Join the Collector List to preview it first.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcoming.map((art) => (
                <CatalogCard key={art.id} art={art} variant="upcoming" onImageClick={setZoomArt} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-14 px-6 md:px-12 lg:px-24 border-t border-white/5 bg-luxury-charcoal">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/#mailing-list" className="btn-gold w-full sm:w-auto font-sans-luxury text-[11px] uppercase tracking-[0.2em] font-semibold px-8 py-4 rounded-none flex items-center justify-center gap-2" data-testid="upcoming-cta-collector">
            <Mail size={14} /> Join the Collector List
          </Link>
          <Link to="/consign" className="btn-ghost-gold w-full sm:w-auto font-sans-luxury text-[11px] uppercase tracking-[0.2em] px-8 py-4 rounded-none flex items-center justify-center gap-2" data-testid="upcoming-cta-consign">
            <Ship size={14} /> Consign Your Collection
          </Link>
        </div>
      </section>

      <Footer />

      {/* Click-to-zoom lightbox (desktop + mobile) */}
      {zoomArt && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10"
          onClick={closeZoom}
          data-testid="image-lightbox"
        >
          <button
            type="button"
            onClick={closeZoom}
            className="absolute top-5 right-5 text-white/80 hover:text-luxury-gold border border-white/20 hover:border-luxury-gold rounded-full p-2 transition-colors duration-300"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={22} />
          </button>
          <div className="max-w-full max-h-full flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={zoomArt.image}
              alt={zoomArt.title}
              className="max-w-[95vw] max-h-[80vh] object-contain"
              data-testid="lightbox-image"
            />
            <p className="text-luxury-goldLight font-serif-luxury text-center text-sm md:text-base px-4">{zoomArt.title}</p>
          </div>
        </div>
      )}
    </div>
  );
}
